class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.liveRegionTimeout = null;
    this.lastAnnouncedText = '';
    this.debounceTimeout = null;

    this.setupEventListeners();
  }

  isMobile() {
    return window.innerWidth <= 767;
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    
    // Add custom tab navigation
    document.addEventListener('keydown', this.handleCustomTabNavigation.bind(this));
    
    // Safari-specific focus handling
    this.addEventListener('focus', this.handleSafariFocus.bind(this), true);
  }

  handleSafariFocus(event) {
    // Prevent Safari from scrolling the entire page when focusing predictive search items
    if (event.target.classList.contains('predictive-search__item')) {
      event.preventDefault();
      // Use smooth scrolling to the element if needed
      setTimeout(() => {
        event.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, 0);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      // Clear the results container to prevent stale data
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.isMobile()) return;
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'Enter':
        if (document.activeElement && document.activeElement.classList.contains('predictive-search__item')) {
          // Let the natural link/button behavior handle the click
          return;
        }
        break;
    }
  }

  onKeydown(event) {
    // No special handling needed for arrow keys anymore
    // Let natural tab navigation work
  }

  handleCustomTabNavigation(event) {
    if (event.key !== 'Tab') return;

    const activeElement = document.activeElement;
    
    // Get all focusable elements in the search modal in proper order
    const resetButton = this.querySelector('.reset__button.field__button:not(.hidden)');
    const searchButton = this.querySelector('.search__button.field__button');
    const closeButton = this.querySelector('.search-modal__close-button');
    
    // Get predictive search suggestions only when open
    const allSuggestions = this.getAttribute('open') ? 
      Array.from(this.querySelectorAll('.predictive-search__item[tabindex="0"]')).filter(
        item => item.offsetParent !== null
      ) : [];
    
    // Build the complete tab order
    const allFocusableElements = [
      this.input,
      resetButton,
      searchButton,
      closeButton,
      ...allSuggestions
    ].filter(Boolean); // Remove null/undefined elements
    
    const currentIndex = allFocusableElements.indexOf(activeElement);
    
    // If current element is not in our managed list, let browser handle it
    if (currentIndex === -1) return;
    
    event.preventDefault();
    
    let nextIndex;
    if (event.shiftKey) {
      // Shift+Tab: go to previous element or wrap to last
      nextIndex = currentIndex === 0 ? allFocusableElements.length - 1 : currentIndex - 1;
    } else {
      // Tab: go to next element or wrap to first
      nextIndex = currentIndex === allFocusableElements.length - 1 ? 0 : currentIndex + 1;
    }
    
    this.focusElement(allFocusableElements[nextIndex]);
  }

  focusElement(element) {
    if (!element) return;
    
    // Safari-safe focus method
    try {
      element.focus({ preventScroll: true });
      
      // For Safari, ensure the element is properly in view without scrolling the page
      setTimeout(() => {
        if (element.scrollIntoView) {
          element.scrollIntoView({ 
            block: 'nearest', 
            inline: 'nearest', 
            behavior: 'smooth' 
          });
        }
      }, 0);
    } catch (e) {
      // Fallback for older browsers
      element.focus();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  announceNoResultsAndFullyBlurInput() {
    if (!this.statusElement) return;
  
    const message = this.getAttribute('data-no-results-text');
    if (!message) {
      return;
    }
    
    // Clear any existing announcements first
    clearTimeout(this.liveRegionTimeout);
    clearTimeout(this.debounceTimeout);
    
    // Force the announcement by resetting the last announced text
    this.lastAnnouncedText = '';
    
    // Use immediate announcement for no results to ensure it's heard
    this.statusElement.setAttribute('aria-atomic', 'true');
    this.statusElement.textContent = '';
    this.statusElement.setAttribute('aria-hidden', 'true');
    
    // Small delay then announce immediately
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = message;
      this.lastAnnouncedText = message;
      
      // Keep announcement visible longer
      this.liveRegionTimeout = setTimeout(() => {
        this.statusElement.textContent = '';
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 4000);
    }, 100);
  }  

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    // Abort any existing request to prevent race conditions
    this.abortController.abort();
    this.abortController = new AbortController();

    if (this.cachedResults[queryKey]) {
      // Add a small delay to ensure the DOM is ready
      setTimeout(() => {
        this.renderSearchResults(this.cachedResults[queryKey]);
      }, 10);
      return;
    }
    
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        
        // Only cache and process if this is still the current search term
        if (searchTerm === this.searchTerm) {
          // Save bandwidth keeping the cache in all instances synced
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup);
        }
      })
      .catch((error) => {
        if (error?.code === 20 || error.name === 'AbortError') {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    // Ensure proper live region attributes for complete phrase announcement
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-live', 'polite');
      this.statusElement.setAttribute('aria-atomic', 'true');
      this.statusElement.setAttribute('role', 'status');
    }

    // Don't announce loading state - only announce final results
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    
    clearTimeout(this.liveRegionTimeout);
    clearTimeout(this.debounceTimeout);
    
    // Don't announce if it's the same text as last time
    if (statusText === this.lastAnnouncedText) return;
    
    // Shorter debounce for faster response when typing stops
    this.debounceTimeout = setTimeout(() => {
      this.lastAnnouncedText = statusText;
      
      // Ensure aria-atomic is set for complete phrase announcement
      this.statusElement.setAttribute('aria-atomic', 'true');
      
      // Clear first to ensure fresh announcement
      this.statusElement.textContent = '';
      this.statusElement.setAttribute('aria-hidden', 'true');
      
      // Small delay to ensure screen reader registers the clear
      setTimeout(() => {
        // Announce only the final search response results to screen readers
        this.statusElement.setAttribute('aria-hidden', 'false');
        this.statusElement.textContent = statusText;
        
        // Keep announcement longer to prevent being overridden by input value
        this.liveRegionTimeout = setTimeout(() => {
          this.statusElement.textContent = '';
          this.statusElement.setAttribute('aria-hidden', 'true');
        }, 4000);
      }, 100);
    }, 200);
  }
  

  renderSearchResults(resultsMarkup) {
    // Ensure we only process results for the current search term
    const currentSearchTerm = this.getQuery();
    if (currentSearchTerm !== this.searchTerm) {
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    
    // Ensure all suggestion items are focusable
    this.initializeFocusableElements();
  
    // More robust check for results - look for multiple possible selectors
    const resultElements = this.querySelectorAll('.predictive-search__list-item[role="option"], .predictive-search__item, .predictive-search__result');
    const hasResults = Array.from(resultElements).some((el) => el.offsetParent !== null);
    
    // Also check if there's any actual content in the results
    const hasContent = this.predictiveSearchResults.textContent.trim().length > 0;
    const hasActualResults = hasResults && hasContent;
  
    if (hasActualResults) {
      // Only announce the final search results
      this.setLiveRegionResults();
      this.open();
    } else {
      // Only announce no results for the final response
      this.announceNoResultsAndFullyBlurInput(); 
      this.close();
    }
  }

  initializeFocusableElements() {
    // Ensure all predictive search items are focusable
    const allItems = this.querySelectorAll('.predictive-search__item');
    allItems.forEach(item => {
      item.setAttribute('tabindex', '0');
      
      // Add specific focus handling for Safari compatibility
      item.addEventListener('focus', (e) => {
        // Prevent Safari from scrolling when focusing elements
        if (e.target.scrollIntoView) {
          e.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
      
      // Ensure proper keyboard handling
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (item.tagName === 'BUTTON') {
            item.click();
          } else {
            const link = item.querySelector('a') || item;
            if (link.href) {
              window.location.href = link.href;
            }
          }
        }
      });
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
  
    // Add a small delay to ensure the DOM is fully updated
    setTimeout(() => {
      // Get the results count text from the actual search response
      const liveRegion = document.querySelector('[data-predictive-search-live-region-count-value]');
      
      let text = '';
      if (liveRegion) {
        text = liveRegion.textContent;
      } else {
        // Fallback: try to count results manually
        const resultElements = this.querySelectorAll('.predictive-search__list-item[role="option"], .predictive-search__item, .predictive-search__result');
        const visibleResults = Array.from(resultElements).filter(el => el.offsetParent !== null);
        if (visibleResults.length > 0) {
          text = `${visibleResults.length} result${visibleResults.length !== 1 ? 's' : ''} found`;
        }
      }
      
      // Only announce the final search response results
      if (text && text.trim()) {
        // Clear any existing announcements first
        clearTimeout(this.liveRegionTimeout);
        clearTimeout(this.debounceTimeout);
        
        // Force the announcement by resetting the last announced text
        this.lastAnnouncedText = '';
        
        // Use the debounced method for results
        this.setLiveRegionText(text);
      }
    }, 50);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.parentElement.closest('.search-modal').classList.add('search-modal--open');
    trapFocus(this);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    if(this.parentElement.closest('.search-modal').classList.contains('search-modal--open')){
      this.parentElement.closest('.search-modal').classList.remove('search-modal--open');
    }
  }

  closeResults(clearSearchTerm = false) {
    clearTimeout(this.liveRegionTimeout);
    clearTimeout(this.debounceTimeout);

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      // Clear any pending announcements when search is cleared
      this.lastAnnouncedText = '';
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    if(this.parentElement.closest('.search-modal').classList.contains('search-modal--open')){
      this.parentElement.closest('.search-modal').classList.remove('search-modal--open');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
